<template>
  <div flat class="pa-4" style="margin: auto;">
      <!-- Loading State -->
      <v-progress-linear v-if="loadingProp" indeterminate color="primary" class="mb-4"></v-progress-linear>

      <!-- Empty State -->
      <div v-else-if="!hasData" class="text-center py-8">
        <v-icon large color="grey lighten-1" class="mb-2">mdi-chart-donut</v-icon>
        <div class="text-body-1 grey--text">No hay datos disponibles</div>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Breadcrumb Navigation -->
        <div class="d-flex align-center mb-4 flex-wrap">
          <v-btn small text @click="resetToTopLevel" :disabled="drillDownPath.length === 0" class="mr-2 mb-1">
            <v-icon small left>mdi-home</v-icon>
            Todo
          </v-btn>

          <div v-for="(level, index) in drillDownPath" :key="level.field + level.value"
            class="d-flex align-center mb-1">
            <v-icon small class="mx-1">mdi-chevron-right</v-icon>
            <v-btn small text @click="goBackToLevel(index)">
              {{ formatHeader(level.field) }}: {{ level.value }}
            </v-btn>
          </div>
        </div>

        <div class="d-flex align-center justify-space-between mb-4">
          <div class="text-h6 font-weight-bold">
            {{ currentLevelLabel }}
          </div>
          <v-chip small color="primary" class="font-weight-medium">
            Total: {{ totalValue }} KG{{ totalValue > 1 ? 's' : '' }}
          </v-chip>
        </div>
        <!-- DONUT CHART -->
        <v-row>
        <v-col cols="12" md="8">
        <div class="chart-container mt-6">
          <div ref="chart" class="chart"></div>

          <!-- Legend -->
          <div v-if="chartData.length > 0" class="legend-container mt-4">
            <v-chip v-for="(item, index) in chartData" :key="item.label" small :color="getColor(index)"
              text-color="white" class="mr-2 mb-2 legend-chip" :class="{ 'active-legend': activeSegment === index }"
              @click="toggleHighlight(index)" style="cursor: pointer; transition: all 0.3s ease;">
              {{ item.label }}:  {{ item.value }} KG{{ item.value > 1 ? 's' : '' }}
            </v-chip>
          </div>
        </div>
        </v-col>
        <v-col cols="12" md="4">
        <!-- DRAGGABLE PARA ORDENAMIENTO -->
        <draggable v-model="internalHeaders" @end="saveOrder" class="draggable-container" animation="200"
          handle=".drag-handle">
          <transition-group name="list">
            <div v-for="(h, i) in internalHeaders" :key="h" class="d-flex align-center mb-2 pa-2 rounded draggable-item"
              :class="{ 'primary lighten-5': i === currentLevelIndex }">
              <v-icon small class="mr-2 drag-handle" color="grey darken-2">
                mdi-drag
              </v-icon>
              <div class="flex-grow-1 font-weight-medium">
                {{ formatHeader(h) }}
                <v-chip v-if="i === currentLevelIndex" x-small color="primary" class="ml-1">
                  nivel {{ i + 1 }}
                </v-chip>
              </div>
              <v-btn v-if="i !== currentLevelIndex && drillDownPath.length === 0" icon small @click="setActiveHeader(h)"
                title="Usar este campo para el gráfico">
                <v-icon small>mdi-chart-pie</v-icon>
              </v-btn>
            </div>
          </transition-group>
        </draggable>
        
        </v-col>
        </v-row>
      </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import * as d3 from "d3";

export default {
  name: "CackeReport",
  components: { draggable },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    headers: {
      type: Array,
      default: () => [],
    },
    valueField: {
      type: String,
      default: "cantidad",
    },
    storageKey: {
      type: String,
      default: "cake-report-order",
    },
    loadingProp: {
      type: Boolean,
      default: false,
    },
    showLegend: {
      type: Boolean,
      default: true,
    },
    showTotal: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      internalHeaders: [],
      drillDownPath: [],
      activeSegment: null,
      currentColors: [],
      svg: null,
      tooltip: null,
      chartInitialized: false,
      isTransitioning: false
    };
  },
  computed: {
    hasData() {
      return this.rows.length > 0 &&
        this.internalHeaders?.length > 0 &&
        this.internalHeaders.some(h => h !== this.valueField);
    },

    filteredRows() {
      if (this.drillDownPath.length === 0) return this.rows;

      return this.rows.filter(row => {
        return this.drillDownPath.every(level => {
          return row[level.field] === level.value;
        });
      });
    },

    currentLevelIndex() {
      return Math.min(this.drillDownPath.length, this.internalHeaders.length - 1);
    },

    currentLevelField() {
      return this.internalHeaders[this.currentLevelIndex];
    },

    chartData() {
      if (!this.hasData || this.filteredRows.length === 0) return [];

      const field = this.currentLevelField;
      if (!field || field === this.valueField) return [];

      const aggregated = {};

      this.filteredRows.forEach((row) => {
        const key = row[field]?.toString() || "Sin dato";
        const value = Number(row[this.valueField]) || 0;
        aggregated[key] = (aggregated[key] || 0) + value;
      });

      const result = Object.entries(aggregated)
        .map(([label, value]) => ({ label, value }))
        .filter(item => item.value > 0)
        .sort((a, b) => b.value - a.value);

      return result;
    },

    currentLevelLabel() {
      if (this.drillDownPath.length === 0) {
        return "Distribución por " + this.formatHeader(this.currentLevelField);
      }

      const currentLevel = this.drillDownPath[this.drillDownPath.length - 1];
      return `Distribución de ${this.formatHeader(this.currentLevelField)} en ${currentLevel.value}`;
    },

    totalValue() {
      return this.chartData.reduce((sum, item) => sum + item.value, 0);
    },

    // Identificador único para forzar nuevos colores por nivel
    colorSeed() {
      return this.drillDownPath.map(level => level.field + level.value).join('|');
    }
  },
  watch: {
    headers: {
      immediate: true,
      handler(val) {
        if (!val || val.length === 0) {
          this.internalHeaders = [];
          return;
        }

        const filteredHeaders = val.filter(h => h !== this.valueField);
        const savedOrder = localStorage.getItem(this.storageKey);

        if (savedOrder) {
          const parsed = JSON.parse(savedOrder);
          this.internalHeaders = parsed.filter(h => filteredHeaders.includes(h));
          filteredHeaders.forEach(h => {
            if (!this.internalHeaders.includes(h)) {
              this.internalHeaders.push(h);
            }
          });
        } else {
          this.internalHeaders = [...filteredHeaders];
        }

        this.generateColors();
        this.$nextTick(() => this.initializeChart());
      },
    },

    chartData: {
      handler(newData, oldData) {
        if (!this.isTransitioning) {
          if (this.chartInitialized) {
            this.updateChart();
          } else {
            this.$nextTick(() => this.initializeChart());
          }
        }
      },
      deep: false
    },

    colorSeed: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.generateColors();
          if (this.chartInitialized && !this.isTransitioning) {
            this.updateChartColors();
          }
        }
      }
    },

    hasData: {
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            if (!this.chartInitialized) {
              this.initializeChart();
            }
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    getContrastColor(hslColor) {
      // Espera formato hsl(h, s%, l%)
      try {
        const parts = hslColor.replace(/hsl\(|\)/g, '').split(',').map(p => p.trim());
        const l = parseFloat(parts[2]);
        return l > 60 ? '#222' : '#fff';
      } catch (e) {
        return '#fff';
      }
    },
    generateColors() {
      // Colores consistentes basados en el label de cada segmento
      this.currentColors = this.chartData.map((item, i) => {
        // Usar el label para generar un color único y consistente
        const labelHash = this.hashString(item.label);
        const hue = labelHash % 360;
        const saturation = 60 + (labelHash % 25);
        const lightness = 45 + (labelHash % 20);
        return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
      });
    },

    hashString(str) {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash);
    },

    formatHeader(header) {
      return header
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },

    setActiveHeader(header) {
      if (this.drillDownPath.length === 0) {
        const index = this.internalHeaders.indexOf(header);
        if (index > 0) {
          this.internalHeaders.splice(index, 1);
          this.internalHeaders.unshift(header);
          this.saveOrder();
        }
      }
    },

    saveOrder() {
      localStorage.setItem(this.storageKey, JSON.stringify(this.internalHeaders));
    },

    getColor(index) {
      return this.currentColors[index % this.currentColors.length] || '#ccc';
    },

    toggleHighlight(index) {
      if (this.activeSegment === index) {
        this.activeSegment = null;
      } else {
        this.activeSegment = index;
      }
      this.updateHighlight();
    },

    updateHighlight() {
      if (!this.svg) return;

      this.svg.selectAll("path")
        .transition()
        .duration(300)
        .style("opacity", (d, i) =>
          this.activeSegment === null || this.activeSegment === i ? 1 : 0.3
        );
    },

    drillDown(segmentData) {
      if (this.currentLevelIndex >= this.internalHeaders.length - 1) return;

      console.log('DrillDown:', {
        from: this.currentLevelField,
        value: segmentData.label,
        currentData: this.chartData.length,
        totalValue: this.totalValue
      });

      this.isTransitioning = true;
      this.hideTooltip();
      this.activeSegment = null;

      this.drillDownPath.push({
        field: this.currentLevelField,
        value: segmentData.label
      });

      this.$nextTick(() => {
        this.isTransitioning = false;
        console.log('After DrillDown:', {
          newField: this.currentLevelField,
          filteredRows: this.filteredRows.length,
          newChartData: this.chartData.length,
          items: this.chartData.map(d => `${d.label}: ${d.value}`)
        });
        this.updateChart();
      });
    },

    goBackToLevel(levelIndex) {
      this.isTransitioning = true;
      this.hideTooltip();
      this.activeSegment = null;

      this.drillDownPath = this.drillDownPath.slice(0, levelIndex);

      this.$nextTick(() => {
        this.isTransitioning = false;
        this.updateChart();
      });
    },

    resetToTopLevel() {
      this.isTransitioning = true;
      this.hideTooltip();
      this.activeSegment = null;
      this.drillDownPath = [];

      this.$nextTick(() => {
        this.isTransitioning = false;
        this.updateChart();
      });
    },

    initializeChart() {
      if (!this.$refs.chart || this.isTransitioning) return;

      if (this.chartData.length === 0) {
        console.log('No chart data available', {
          hasData: this.hasData,
          filteredRowsCount: this.filteredRows.length,
          currentLevelField: this.currentLevelField,
          currentLevelIndex: this.currentLevelIndex,
          drillDownPathLength: this.drillDownPath.length
        });
        return;
      }

      // Limpiar solo si no está inicializado
      if (!this.chartInitialized) {
        this.$refs.chart.innerHTML = "";
        this.hideTooltip();
      }

      const width = 300;
      const height = 300;
      const radius = Math.min(width, height) / 2;

      // Crear SVG solo si no existe
      if (!this.svg) {
        this.svg = d3.select(this.$refs.chart)
          .append("svg")
          .attr("width", width)
          .attr("height", height)
          .append("g")
          .attr("transform", `translate(${width / 2}, ${height / 2})`);

        // Crear tooltip una sola vez
        this.tooltip = d3.select("body")
          .append("div")
          .attr("class", "chart-tooltip")
          .style("position", "absolute")
          .style("background", "rgba(0,0,0,0.9)")
          .style("color", "white")
          .style("padding", "8px 12px")
          .style("border-radius", "6px")
          .style("font-size", "12px")
          .style("pointer-events", "none")
          .style("opacity", 0)
          .style("z-index", "9999")
          .style("font-family", "Arial, sans-serif")
          .style("box-shadow", "0 2px 10px rgba(0,0,0,0.3)")
          .style("transition", "opacity 0.2s ease");
      }

      this.renderChartData();
      this.chartInitialized = true;
    },

    renderChartData() {
      if (!this.svg) return;

      // Regenerar colores justo antes de render si la longitud cambió
      this.generateColors();

      let data = this.chartData.slice();
      const total = data.reduce((s, d) => s + d.value, 0);

      // Agrupar segmentos demasiado pequeños para evitar slices invisibles
      const MIN_PERCENT = 0.005; // 0.5%
      const small = data.filter(d => d.value / total < MIN_PERCENT);
      if (small.length > 1) {
        const othersValue = small.reduce((s, d) => s + d.value, 0);
        data = data.filter(d => d.value / total >= MIN_PERCENT);
        data.push({ label: 'Otros', value: othersValue });
      }

      const radius = 150;
      const pie = d3.pie()
        .value(d => d.value)
        .sort(null)
        .padAngle(data.length === 1 ? 0.0001 : 0);
      const sliceData = pie(data);

      // Caso especial: un solo segmento -> ajustar ángulos y evitar transición que genera error
      const isSingle = sliceData.length === 1;
      if (isSingle) {
        sliceData[0].startAngle = 0.0001;
        sliceData[0].endAngle = Math.PI * 2 - 0.0001;
      }

      const arc = d3.arc()
        .innerRadius(radius * 0.5)
        .outerRadius(radius - 10);

      const paths = this.svg.selectAll("path")
        .data(sliceData, d => d.data.label);

      paths.exit().remove();

      const enterPaths = paths.enter()
        .append("path")
        .attr("fill", (d, i) => this.getColor(i))
        .attr("stroke", "#fff")
        .attr("stroke-width", 2)
        .style("cursor", "pointer")
        .style("opacity", 1)
        .style("transition", "all 0.3s ease")
        .on("mouseover", (event, d) => {
          if (!this.isTransitioning) {
            this.showTooltip(event, d);
            d3.select(event.currentTarget)
              .transition()
              .duration(150)
              .attr("stroke-width", 4)
              .style("filter", "brightness(1.15)");
          }
        })
        .on("mousemove", (event) => {
          if (!this.isTransitioning) this.moveTooltip(event);
        })
        .on("mouseout", (event) => {
          this.hideTooltip();
          d3.select(event.currentTarget)
            .transition()
            .duration(150)
            .attr("stroke-width", 2)
            .style("filter", "none");
        })
        .on("click", (event, d) => {
          this.hideTooltip();
          this.drillDown(d.data);
        });

      const allPaths = enterPaths.merge(paths);

      // Aplicar eventos nuevamente por seguridad
      allPaths
        .on("mouseover", (event, d) => {
          if (!this.isTransitioning) {
            this.showTooltip(event, d);
            d3.select(event.currentTarget)
              .transition()
              .duration(150)
              .attr("stroke-width", 4)
              .style("filter", "brightness(1.15)");
          }
        })
        .on("mousemove", (event) => {
          if (!this.isTransitioning) this.moveTooltip(event);
        })
        .on("mouseout", (event) => {
          this.hideTooltip();
          d3.select(event.currentTarget)
            .transition()
            .duration(150)
            .attr("stroke-width", 2)
            .style("filter", "none");
        })
        .on("click", (event, d) => {
          this.hideTooltip();
          this.drillDown(d.data);
        });

      if (isSingle) {
        console.log("Single segment - applying direct attributes", {
          label: sliceData[0].data.label,
          value: sliceData[0].data.value
        });

        // Sin transición para evitar attrTween bug
        allPaths.attr("d", arc).attr('fill', (d, i) => this.getColor(i));
        // Fallback por si algo sale mal y no hay path
        if (allPaths.size() === 0) {
          this.svg.append('circle')
            .attr('r', radius - 10)
            .attr('fill', this.getColor(0));
        }


      } else {
        allPaths
          .transition()
          .duration(500)
          .attr("d", arc)
          .attr("fill", (d, i) => this.getColor(i));
      }

      this.updateCenterText();

      // Etiquetas dentro de los segmentos
      this.svg.selectAll('.slice-label').remove();
      const LABEL_THRESHOLD = 0.03; // 3% mínimo para mostrar label completo
      sliceData.forEach((d, i) => {
        const percent = d.data.value / total;
        const [x, y] = arc.centroid(d);
        if (percent >= LABEL_THRESHOLD || sliceData.length === 1) {
          const color = this.getColor(i);
          const textColor = this.getContrastColor(color);
          const group = this.svg.append('g')
            .attr('class', 'slice-label')
            .attr('transform', `translate(${x},${y})`);

          const labelLines = [];
          // Si el slice es relativamente grande mostramos label + porcentaje, sino sólo porcentaje
          if (percent >= LABEL_THRESHOLD * 2 || sliceData.length === 1) {
            labelLines.push(d.data.label);
          }
          labelLines.push((percent * 100).toFixed(1) + '%');

          labelLines.forEach((line, idx) => {
            group.append('text')
              .attr('text-anchor', 'middle')
              .attr('dy', `${idx * 1.1}em`)
              .style('font-size', sliceData.length === 1 ? '14px' : '11px')
              .style('font-weight', idx === 0 ? '600' : '500')
              .style('pointer-events', 'none')
              .style('fill', textColor)
              .text(line);
          });
        }
      });
    },

    updateChart() {
      if (this.chartData.length === 0) {
        console.log('UpdateChart: No data to render');
        return;
      }
      this.renderChartData();
      this.updateHighlight();
    },

    updateChartColors() {
      if (!this.svg) return;

      this.svg.selectAll("path")
        .transition()
        .duration(500)
        .attr("fill", (d, i) => this.getColor(i));
    },

    updateCenterText() {
      if (!this.svg) return;

      // Remover textos existentes
      this.svg.selectAll(".center-text").remove();

      // Agregar textos
      this.svg.append("text")
        .attr("class", "center-text")
        .attr("text-anchor", "middle")
        // .attr("dy", "-0.5em")
        .attr("dy", "0.6em")
        .style("font-size", "12px")
        .style("font-weight", "bold")
        .style("pointer-events", "none")
        .text(`TOTAL: ${this.totalValue} KG${this.totalValue > 1 ? 's' : ''}`);

      // this.svg.append("text")
      //   .attr("class", "center-text")
      //   .attr("text-anchor", "middle")
      //   .attr("dy", "1.2em")
      //   .style("font-size", "12px")
      //   .style("fill", "#666")
      //   .style("pointer-events", "none")
      //   .text("Total");

      // Indicador de drill-down
      if (this.currentLevelIndex < this.internalHeaders.length - 1) {
        this.svg.append("text")
          .attr("class", "center-text")
          .attr("text-anchor", "middle")
          .attr("dy", "2.5em")
          .style("font-size", "10px")
          .style("fill", "#999")
          .style("pointer-events", "none")
          /* .text("Click para detallar") */;
      }
    },

    showTooltip(event, d) {
      if (this.isTransitioning) return;

      const percentage = ((d.data.value / this.totalValue) * 100).toFixed(1);
      this.tooltip
        .interrupt()
        .html(`${d.data.label}<br/><strong>${d.data.value} (${percentage}%)</strong>`)
        .transition()
        .duration(200)
        .style("opacity", 1);

      this.moveTooltip(event);
    },

    moveTooltip(event) {
      this.tooltip
        .style("left", (event.pageX + 15) + "px")
        .style("top", (event.pageY - 15) + "px");
    },

    hideTooltip() {
      if (this.tooltip) {
        this.tooltip
          .interrupt()
          .transition()
          .duration(200)
          .style("opacity", 0);
      }
    }
  },

  beforeDestroy() {
    // Limpiar tooltip global
    if (this.tooltip) {
      this.tooltip.interrupt().remove();
      this.tooltip = null;
    }

    // Limpiar SVG
    if (this.svg) {
      d3.select(this.$refs.chart).selectAll("*").remove();
      this.svg = null;
    }

    this.chartInitialized = false;
  },
};
</script>

<style scoped>
.draggable-container {
  border: 1px dashed #ccc;
  border-radius: 8px;
  padding: 8px;
}

.draggable-item {
  background: #fafafa;
  border: 1px solid #eee;
  transition: all 0.2s ease;
}

.draggable-item:hover {
  background: #f0f0f0;
  transform: translateY(-1px);
}

.drag-handle {
  cursor: grab;
}

.chart-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chart {
  display: flex;
  justify-content: center;
}

.legend-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 600px;
}

.legend-chip {
  transition: all 0.3s ease;
}

.legend-chip.active-legend {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1;
}

.list-move {
  transition: transform 0.3s ease;
}
</style>
